import type React from "react";
import { useState } from "react";

import type { FeelingAtHomeSurveyQuestionV2Dto } from "@/api/types";
import { HistogramChart, type HistogramChartDataPoint } from "@/components/Charts/HistogramChart";
import { NoData } from "@/components/NoData/NoData";
import { Button } from "@/design-system/Button";
import { Columns } from "@/design-system/Columns";
import { SectionCard } from "@/design-system/SectionCard";
import { Spinner } from "@/design-system/Spinner";
import { useBool } from "@/hooks/useBool";
import { useTranslation } from "@/translations";

import { FahHistoryQuestionResponsesModal } from "./FahHistoryQuestionResponsesModal";
import {
  SurveyQuestionGroupAnswerCardHeader,
  SurveyQuestionGroupAnswerCardRating,
} from "./SurveyQuestionGroupAnswerCard";

interface FahHistoryQuestionGroupCardProps {
  order: number;
  question: FeelingAtHomeSurveyQuestionV2Dto;
  isLoading?: boolean;
}

export function FahHistoryQuestionGroupCard({
  order,
  question,
  isLoading,
}: FahHistoryQuestionGroupCardProps): React.ReactNode {
  const [filter, setFilter] = useState<{ rating?: number }>();
  const [isResponsesModalOpened, responsesModalHandlers] = useBool();

  const { t } = useTranslation();

  const getHistogramData = (data: Record<string, number>) => {
    const graphData: HistogramChartDataPoint[] = [];

    for (let i = 2; i <= 10; i += 2) {
      graphData.push({ category: String(i), value: data[String(i)] || 0 });
    }

    return graphData;
  };

  const handleClickBar = (dataPoint: HistogramChartDataPoint) => {
    const rating = Number(dataPoint.category);

    setFilter((prev) => ({ ...prev, rating }));
    responsesModalHandlers.setTrue();
  };

  const histogramData = getHistogramData(question.ratingGroupedNumber);
  const isDataAvailable = histogramData.some((dataPoint) => Boolean(dataPoint.value));
  const filteredResponses = question.responses.filter((response) => {
    if (filter?.rating) {
      return response.rating === filter.rating;
    }

    return true;
  });

  return (
    <>
      <SectionCard>
        <SurveyQuestionGroupAnswerCardHeader
          questionTitle={question.ratingQuestionText || ""}
          category={question.category}
          {...{ order }}
        />
        <Columns gap="md" collapseBelow="md">
          <section className="min-w-0 flex-1 @md:pr-space-sm">
            {isDataAvailable ? (
              <HistogramChart data={histogramData} onClickBar={handleClickBar} />
            ) : (
              <NoData error={null} />
            )}
          </section>
          <section className="flex min-w-0 flex-1 items-center @md:border-l @md:border-border-default @md:pl-space-sm">
            <SurveyQuestionGroupAnswerCardRating rating={question.averageRating} />
          </section>
        </Columns>

        {isDataAvailable && (
          <footer className="mt-space-md flex w-full flex-wrap justify-end gap-space-sm">
            <Button variant="filled" onClick={responsesModalHandlers.setTrue}>
              {t("component.surveys-question-group-answer.button.details")}
            </Button>
          </footer>
        )}

        {isLoading && (
          <aside className="absolute inset-0 flex items-center justify-center bg-background-default/50">
            <Spinner size="2xl" />
          </aside>
        )}
      </SectionCard>

      <FahHistoryQuestionResponsesModal
        isOpened={isResponsesModalOpened}
        onOpenChange={responsesModalHandlers.set}
        order={order}
        question={{
          ...question,
          responses: filteredResponses,
        }}
      />
    </>
  );
}
